import React, { useEffect, useState, useCallback } from 'react';

import { Button } from "components/ui/button";
import { Link } from "react-router-dom";

import PageTitle from '../components/generic/PageTitle';
import Breadcrumbs from 'components/generic/Breadcrumbs';
import Tabs from 'components/generic/FilterTabs';
import LiveStreamCard from 'components/livestream/LivestreamCard';
import { getLivestreamProjects } from 'services/livestreamService';

const LivestreamPage = () => {
  const [loading, setLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState("Upcoming Streamings");
  const [livestreamProjects, setLivestreamProjects] = useState([]);
  const [error, setError] = useState(null);

  const [page, setPage] = useState(1);
  const limit = 50;
  const [hasMore, setHasMore] = useState(false);

  const fetchLivestreams = useCallback(async (isLoadMore = false) => {
    setLoading(true);
    setError(null);

    const tabParam = selectedTab === "Upcoming Streamings" ? 'upcoming' : 'completed';
    const currentPage = isLoadMore ? page + 1 : 1;

    try {
      const response = await getLivestreamProjects(tabParam, currentPage, limit);

      if (response.success) {
        const newData = response.data || [];
        if (isLoadMore) {
          setLivestreamProjects(prev => [...prev, ...newData]);
          setPage(currentPage);
        } else {
          setLivestreamProjects(newData);
          setPage(1);
        }

        // Handle pagination
        if (response.pagination) {
          setHasMore(response.pagination.currentPage < response.pagination.totalPages);
        } else {
          setHasMore(newData.length === limit);
        }
      } else {
        setError(response.message || "Failed to load livestreams");
      }
    } catch (err) {
      setError("An unexpected error occurred");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [selectedTab, page, limit]);

  useEffect(() => {
    fetchLivestreams();
  }, [selectedTab]);

  const loadMore = () => {
    fetchLivestreams(true);
  };

  return (
    <div className="crew-mngt-content p-6">
      <Breadcrumbs />
      <div className="flex flex-wrap justify-between items-start mb-2">
        <PageTitle
          title="All Livestreams"
          description="Complete overview of all your livestream projects."
        />
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}


      {/* Page Loading - initial */}
      {loading && livestreamProjects.length === 0 ? (
        <div className="flex justify-center items-center py-12">
          <p className="text-muted-foreground">Loading livestreams...</p>
        </div>
      ) : (
        <>
          {/* Filters */}
          <Tabs
            tabs={["Upcoming Streamings", "Completed"]}
            value={selectedTab}
            onChange={(val) => {
              setSelectedTab(val);
              setLivestreamProjects([]);
            }}
          />

          {livestreamProjects.length === 0 && !loading ? (
            /* Empty State */
            <div className="flex flex-col justify-center items-center py-12">
              <p className="text-muted-foreground mb-2">No livestreams found</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {livestreamProjects.map((event) => (
                  <div key={event.project?.stream_project_booking_id} className="w-full">
                    <LiveStreamCard event={event} />
                  </div>
                ))}
              </div>

              {/* VIEW MORE BUTTON */}
              {hasMore && (
                <div className="flex justify-center mt-6">
                  <Button
                    className="bg-button-coloured px-6 py-2"
                    onClick={loadMore}
                    disabled={loading}
                  >
                    {loading ? 'Loading...' : 'View More'}
                  </Button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default LivestreamPage;
